import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatPrice, formatImageUrl } from '../utils/helpers';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { ArrowLeft, ShoppingCart, Sparkles, Calendar, User, MessageSquare } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addServiceToCart } = useCart();
  const { addToast } = useToast();
  
  const [service, setService] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Form state
  const [fullName, setFullName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [questions, setQuestions] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchService();
  }, [id]);

  const fetchService = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/services/${id}`);
      setService(response.data);
      
      // Fetch category info
      const categoriesResponse = await axios.get(`${API_URL}/service-categories`);
      const cat = categoriesResponse.data.find(c => c.id === response.data.category_id);
      setCategory(cat);
    } catch (error) {
      console.error('Failed to fetch service:', error);
      addToast('Szolgáltatás nem található', 'error');
      navigate('/szolgaltatasok');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!fullName.trim()) {
      addToast('Kérjük, adja meg a teljes nevét!', 'error');
      return;
    }
    
    if (!birthDate) {
      addToast('Kérjük, adja meg a születési dátumát!', 'error');
      return;
    }

    setSubmitting(true);
    const result = await addServiceToCart(service.id, fullName, birthDate, questions);
    setSubmitting(false);
    
    if (result.success) {
      addToast('Szolgáltatás hozzáadva a kosárhoz!', 'success');
      // Clear form
      setFullName('');
      setBirthDate('');
      setQuestions('');
      
      // Redirect to cart after 1 second
      setTimeout(() => {
        navigate('/kosar');
      }, 1000);
    } else {
      addToast(result.error, 'error');
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-screen pt-24 pb-12">
        <canvas id="starfield" className="fixed inset-0 z-0 pointer-events-none"></canvas>
        <div className="relative z-10 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-200"></div>
            <p className="mt-4 text-gray-400">Betöltés...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!service) {
    return null;
  }

  return (
    <div className="relative min-h-screen pt-24 pb-12">
      <canvas id="starfield" className="fixed inset-0 z-0 pointer-events-none"></canvas>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/szolgaltatasok')}
          className="flex items-center gap-2 text-gray-400 hover:text-yellow-200 transition mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Vissza a szolgáltatásokhoz
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Images */}
          <div className="space-y-4">
            <div className="glass rounded-2xl overflow-hidden">
              {service.images && service.images.length > 0 ? (
                <img
                  src={formatImageUrl(service.images[currentImageIndex])}
                  alt={service.name}
                  className="w-full h-96 object-cover"
                />
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-purple-900/20 to-black flex items-center justify-center">
                  <Sparkles className="w-20 h-20 text-yellow-200/30" />
                </div>
              )}
            </div>

            {/* Thumbnail Images */}
            {service.images && service.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {service.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`glass rounded-lg overflow-hidden border-2 transition ${
                      currentImageIndex === idx ? 'border-yellow-200' : 'border-transparent'
                    }`}
                  >
                    <img
                      src={formatImageUrl(img)}
                      alt={`${service.name} ${idx + 1}`}
                      className="w-full h-20 object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Service Details & Form */}
          <div className="space-y-6">
            <div className="glass rounded-2xl p-6">
              {category && (
                <span className="inline-block px-3 py-1 bg-yellow-200/10 border border-yellow-200/30 rounded-full text-xs text-yellow-200 mb-4">
                  {category.name}
                </span>
              )}
              
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {service.name}
              </h1>
              
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                {service.description}
              </p>

              <div className="flex items-center gap-4 mb-6">
                <div className="text-4xl font-bold text-yellow-200">
                  {formatPrice(service.price)}
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-yellow-200/5 border border-yellow-200/20 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-300">
                  ✨ <strong>Online szolgáltatás:</strong> A szolgáltatást emailben kapja meg<br/>
                  ⏱️ <strong>Elkészülési idő:</strong> Általában 2 héten belül<br/>
                  💌 <strong>Időpont egyeztetés:</strong> Szükség esetén emailben
                </p>
              </div>
            </div>

            {/* Order Form */}
            <form onSubmit={handleAddToCart} className="glass rounded-2xl p-6 space-y-4">
              <h3 className="text-xl font-semibold text-white mb-4">Megrendelési adatok</h3>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  Teljes név <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Kovács János"
                  required
                  className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                />
              </div>

              {/* Birth Date */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Születési dátum <span className="text-red-400">*</span>
                </label>
                <input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  required
                  className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                />
              </div>

              {/* Questions */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-2" />
                  Kérdések, történet (opcionális)
                </label>
                <textarea
                  value={questions}
                  onChange={(e) => setQuestions(e.target.value)}
                  placeholder="Írja le a kérdéseit, történetét, vagy bármit, ami segítheti a szolgáltatás elkészítését..."
                  rows={5}
                  className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minél részletesebb információt ad, annál pontosabb lesz a szolgáltatás.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full btn-magic rounded-lg px-6 py-3 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                {submitting ? 'Hozzáadás...' : 'Kosárba'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.04), hsl(0 0% 100% / 0.02));
          border: 1px solid hsl(0 0% 100% / 0.06);
          backdrop-filter: blur(6px);
        }
        .btn-magic {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(253, 230, 138, 0.1), rgba(251, 191, 36, 0.1));
          border: 1px solid hsl(0 0% 100% / 0.14);
        }
        .btn-magic:before {
          content: "";
          position: absolute;
          inset: -200%;
          background: conic-gradient(from 0deg, transparent, hsl(45 86% 62% / 0.25), transparent 30%);
          animation: spin 6s linear infinite;
          z-index: -1;
          filter: blur(12px);
        }
        @keyframes spin {
          to { transform: rotate(1turn); }
        }
      `}</style>
    </div>
  );
};

export default ServiceDetail;
